<template>
  <div class="nx-relation-editor">
    <nx-relation-editor-toolbar
      :title="title"
      :help-text="helpText"
      :class="{'mb-3': !collapsable, 'mb-2': collapsable }"
      :bcube-assistant-component="$attrs.bcubeAssistantComponent || $attrs['bcube-assistant-component']"
      @bcube-assistant="$emit('bcube-assistant', $event)"
    >
      <template slot="post-title">
        <v-tooltip
          v-if="foldable"
          top
          class="btn btn--icon"
        >
          <v-btn
            slot="activator"
            icon
            flat
            :disabled="disabled"
            @click="foldableValue = !foldableValue"
          >
            <v-icon v-text="foldableValue ? 'visibility' : 'visibility_off'" />
          </v-btn>
          <span v-html="$t(foldableValue ? 'message.relation.unfold' : 'message.relation.fold')" />
        </v-tooltip>
      </template>
      <template slot="actions">
        <v-tooltip
          v-if="multiple && (!isConfigGenerated || uploadFromZipEnabled)"
          top
          class="btn btn--icon mr-2"
        >
          <v-btn
            v-if="!disableAdd"
            slot="activator"
            icon
            small
            @click="insertNewItemsAtBeginningValue = !insertNewItemsAtBeginningValue"
          >
            <v-icon>{{ insertNewItemsAtBeginningValue ? 'vertical_align_top' : 'vertical_align_bottom' }}</v-icon>
          </v-btn>
          <span
            v-html="$t(`message.search.insertItemsAt${insertNewItemsAtBeginningValue ? 'Beginning' : 'End'}`)"
          />
        </v-tooltip>
        <v-tooltip
          v-if="!isConfigGenerated && (multiple || empty)"
          top
          class="btn btn--icon"
        >
          <v-btn
            v-if="!disableAdd"
            slot="activator"
            :disabled="limitReached || disabled"
            :loading="loading"
            icon
            small
            @click="openContentSearchDialog"
          >
            <v-icon>search</v-icon>
          </v-btn>
          <span
            v-html="limitReached ? $t('message.search.selection.limitReached') : $t('message.relation.search')"
          />
        </v-tooltip>
        <v-tooltip
          v-if="!empty && !disableDelete"
          top
          class="btn btn--icon"
        >
          <v-btn
            slot="activator"
            icon
            small
            :disabled="disabled || loading"
            @click="deleteAll"
          >
            <v-icon>delete</v-icon>
          </v-btn>
          <span
            v-html="$t('message.relation.deleteAll')"
          />
        </v-tooltip>
        <v-tooltip
          v-if="multiple || empty"
          top
          class="btn btn--icon"
        >
          <v-btn
            v-if="!disableAdd"
            slot="activator"
            icon
            small
            :disabled="limitReached || disabled || loading"
            @click="addRelation"
          >
            <v-icon>add</v-icon>
          </v-btn>
          <span
            v-html="limitReached ? $t('message.search.selection.limitReached') : $t('message.relation.add')"
          />
        </v-tooltip>
        <v-tooltip
          v-if="uploadFromZipEnabled && (multiple || empty)"
          top
          class="btn btn--icon"
        >
          <v-btn
            v-if="!disableAdd"
            slot="activator"
            icon
            small
            :disabled="limitReached || disabled || loading"
            @click="openZipUploadDialog"
          >
            <v-icon>perm_media</v-icon>
          </v-btn>
          <span
            v-html="limitReached ? $t('message.search.selection.limitReached') : $t('message.relation.addFromZip')"
          />
        </v-tooltip>
        <slot name="toolbar" />
      </template>
    </nx-relation-editor-toolbar>

    <transition-group
      :id="`form-field-${name}`"
      ref="relations"
      class="relation-list"
      name="relation-editor-list"
      tag="div"
      :class="{ moving: moving }"
    >
      <nx-relation-card
        v-for="(relation, index) in relationCards"
        :key="relation.uuid"
        class="relation-list-card"
        :uuid="relation.uuid"
        :position="index"
        :value="relation"
        :component="config.cardComponent"
        :collapsable="isCollapsable"
        :collapsable-default-value="collapsableDefaultValue"
        :collapsed-title-field="collapsedTitleField"
        :collapsed-image-field="collapsedImageField"
        :collapsed-icon="collapsedIcon"
        :relation-type="relationType"
        :relation-name="name"
        :card-params="config.cardParams"
        :count="internalValue.length"
        :hide-tools="!multiple"
        :disabled="disabled"
        :disable-sort="disableSort"
        :disable-delete="disableDelete"
        :disable-crop="disableCrop"
        :excluded-entity-ids="excludedEntityIds"
        :wizard-types="types"
        :dark-theme="darkTheme"
        @move-down="move(index, index + 1)"
        @move-up="move(index, index - 1)"
        @remove="remove(index)"
        @input="updateRelation"
        @default="updateRelationWithDefaultValue"
        @relationDeleted="relationDeleted"
      />
      <nx-relation-empty-card
        v-if="empty || (foldable && foldableValue)"
        key="empty"
        :folded-num-elements="internalValue.length"
        :folded="foldable && foldableValue"
        @unfold="foldableValue = false"
      />
    </transition-group>

    <nx-searcher-dialog
      v-if="contentSearchDialogState.render"
      :active="contentSearchDialogState.active"
      :entity="entity"
      :entity-tabs="false"
      :filters="filters"
      :limit="limit"
      :value="searcherValue"
      title="Buscador"
      @dismiss="closeContentSearchDialog"
      @input="acceptSearchSelection"
    />

    <nx-relation-editor-zip-uploader
      v-if="zipUploaderDialogState.render"
      :active="zipUploaderDialogState.active"
      :images-title="contentTitle"
      :main-taxonomy-id="contentMainTaxonomyId"
      title=""
      @dismiss="closeZipUploadDialog"
      @input="acceptZipUploader"
    />

    <nx-dialog-confirm
      ref="confirmDeleteAll"
      :title="$t('message.relation.deleteAllTitle')"
      :text="$t('message.relation.deleteAllText')"
      :cancel-text="$t('message.relation.deleteAllCancelText')"
      :accept-text="$t('message.relation.deleteAllAcceptText')"
      :persistent="true"
      accept-color="secondary"
      cancel-color="error"
    />

    <v-messages
      class="mb-1"
      color="error"
      :value="errorMessages"
    />
  </div>
</template>

<script>
import AllowedContentsOnly from '@/modules/searcher/mixins/allowed-contents-only';
import api from '@/modules/relation/api/';
import Form from '@/modules/form/Form';
import generateUuid from 'uuid/v4';
import NxSearcherDialog from '@/modules/searcher/SearcherDialog';
import relationConfig from '@/modules/relation/config';
import {
  cloneDeep, get, isEqual, isEmpty,
} from 'lodash';
import { toArray } from '@/utils';
import NxRelationEditorZipUploader from '@/modules/relation/editor/components/ZipUploader';
import NxRelationEmptyCard from '@/modules/relation/card/EmptyCard';
import NxDialogConfirm from '@/components/dialogs/Confirm';
import NxRelationCard from '@/modules/relation/card/Card';
import NxRelationEditorToolbar from '@/modules/relation/editor/components/Toolbar';
import { mapGetters } from 'vuex';
import Storable from '@/mixins/storable';
import Sortable from 'sortablejs';

export default {
  name: 'NxRelationEditor',
  components: {
    NxRelationEditorToolbar,
    NxRelationCard,
    NxDialogConfirm,
    NxRelationEmptyCard,
    NxRelationEditorZipUploader,
    NxSearcherDialog,
  },
  extends: Form,
  mixins: [AllowedContentsOnly, Storable],
  inject: {
    $getParentEditor: {
      default: null,
    },
  },
  props: {
    allowDuplicatedEntities: {
      type: Boolean,
      default: false,
    },
    cardParams: {
      type: Object,
      default: () => ({}),
    },
    foldable: {
      type: Boolean,
    },
    foldableDefaultValue: {
      type: Boolean,
      default: false,
    },
    collapsable: {
      type: Boolean,
      default: true,
    },
    collapsableDefaultValue: {
      type: Boolean,
      default: true,
    },
    collapsedTitleField: {
      type: String,
      default: null,
    },
    collapsedImageField: {
      type: String,
      default: null,
    },
    collapsedIcon: {
      type: String,
      default: null,
    },
    disabled: {
      type: Boolean,
    },
    disableAdd: {
      type: Boolean,
    },
    disableSort: {
      type: Boolean,
    },
    disableDelete: {
      type: Boolean,
    },
    disableCrop: {
      type: Boolean,
    },
    disableEmptyInitialCard: {
      type: Boolean,
    },
    errorMessages: {
      type: Array,
      default: () => ([]),
    },
    enableUploadFromZip: Boolean,
    limit: {
      type: Number,
      default: null,
    },
    title: {
      type: String,
      required: true,
    },
    name: {
      type: String,
      required: true,
    },
    relationType: {
      type: String,
      required: true,
    },
    types: {
      type: Array,
      default: null,
    },
    wizardTypes: {
      type: Array,
      default: () => ([]),
    },
    value: {
      type: [Array, Object],
      default: () => [],
    },
    insertNewItemsAtBeginning: {
      type: Boolean,
      default: true,
    },
    emptyItemsByDefault: {
      type: Number,
      default: 0,
    },
    helpText: {
      type: String,
      default: null,
    },
    darkTheme: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    internalValue: [],
    contentSearchDialogState: { active: false, render: false },
    zipUploaderDialogState: { active: false, render: false },
    entity: 'content', // Required by Searchable.
    filters: {}, // Required by Searchable
    loading: false,
    insertNewItemsAtBeginningValue: true,
    foldableValue: false,
    moving: false,
    validationEmitted: false,
  }),
  computed: {
    ...mapGetters(['availableContentTypes']),
    storePath() {
      return this.$getParentEditor ? this.$getParentEditor().storePath : ['content', 'editor'];
    },
    limitReached() {
      return this.limit !== null && this.limit <= this.internalValue.length;
    },
    isCollapsable() {
      return this.multiple && this.collapsable;
    },
    multiple() {
      return this.limit !== 1;
    },
    empty() {
      return this.internalValue.length === 0;
    },
    relationCards() {
      if (this.foldable && this.foldableValue) {
        return [];
      }
      return this.internalValue;
    },
    selectedContentIds() {
      return this.internalValue.reduce((acc, item) => {
        if (item.destContent && item.destContent.id) {
          acc.push(item.destContent.id);
          return acc;
        }
        if (item.contentId) {
          acc.push(item.contentId);
          return acc;
        }
        if (item.id) {
          acc.push(item.id);
          return acc;
        }
        return acc;
      }, []);
    },
    excludedEntityIds() {
      if (this.allowDuplicatedEntities) {
        return [];
      }
      const editor = this.$getParentEditor && this.$getParentEditor();
      if (!editor) {
        return this.selectedContentIds;
      }

      const { id, type } = editor;
      if (id && this.availableContentTypes.includes(type)) {
        return [
          ...this.selectedContentIds,
          id,
        ];
      }

      return this.selectedContentIds;
    },
    config() {
      const config = relationConfig[this.relationType] || relationConfig.default;
      // Configure card params
      const cardParams = {
        ...(config.cardParams || {}), // Default configuration for the given relation
        ...{ relationType: this.relationType }, // Relation type
        ...this.cardParams, // Specific card params given for this relation
      };

      let configTypes = this.types;
      if ((!Array.isArray(configTypes) || configTypes.length === 0)
        && Array.isArray(cardParams.types)) {
        configTypes = cardParams.types;
      }
      // Return a unique config object with the default config and the configured card params
      return Object.assign({ types: configTypes }, config, { cardParams });
    },
    searcherValue() {
      return this.internalValue
        .filter(relation => relation.destContent)
        .map(relation => relation.destContent);
    },
    isConfigGenerated() {
      return this.relationType === 'configGenerated';
    },
    contentMainTaxonomyId() {
      if (this.enableUploadFromZip) {
        return get(this.getters.draft, 'mainTaxonomyId.id');
      }
      return null;
    },
    contentTitle() {
      if (this.enableUploadFromZip) {
        return get(this.getters.draft, 'title');
      }
      return null;
    },
    uploadFromZipEnabled() {
      return this.enableUploadFromZip && this.contentMainTaxonomyId && this.contentTitle;
    },
    internalValueWithValidation() {
      return this.internalValue.map(val => ({ ...val, relationValidation: this.form }));
    },
  },
  watch: {
    form(value, oldValue) {
      if (isEqual(value, oldValue) && this.validationEmitted) {
        return;
      }
      this.validationEmitted = true;
      this.$emit('relationValidation', this.internalValueWithValidation);
    },
    excludedEntityIds: {
      handler(val) {
        if (isEmpty(val)) {
          this.$delete(this.filters, 'excludedIds');
          return;
        }
        this.filters = { ...this.filters, excludedIds: val };
      },
      immediate: true,
    },
    async value(newValue) {
      if (!isEqual(toArray(newValue), this.internalValue)) {
        await this.$nextTick();
        this.setInternalValue(newValue);
      }
    },
    types() {
      this.updateTypeFilter(this.config.types);
    },
  },
  mounted() {
    this.insertNewItemsAtBeginningValue = this.insertNewItemsAtBeginning;
    this.setInternalValue(this.value);
    this.updateTypeFilter(this.config.types);
    this.foldableValue = this.foldableDefaultValue;
    if (this.collapsable) {
      this.sortablejs = Sortable.create(this.$refs.relations.$el, {
        handle: '.nx-drag-handle',
        onUpdate: ({ oldIndex, newIndex }) => this.move(oldIndex, newIndex),
        onStart: () => {
          this.moving = true;
        },
        onEnd: () => {
          this.moving = false;
        },
      });
    }
  },
  methods: {
    relationDeleted(uuid) {
      this.internalValue = this.internalValue.filter(item => item.uuid !== uuid);
      this.emitValue();
    },
    async acceptSearchSelection(contents) {
      contents = this.filterPublishedContents(contents, this.$t('message.content.relation.error.nonPublishedContentSelectedWarning'));
      contents = this.filterContentsByType(contents, this.$t('message.content.relation.error.nonAllowedContentTypeSelectedWarning'));
      if (contents.length === 0) {
        return;
      }

      this.loading = true;
      const relations = await this.getRelationFields(contents.map(content => content.id));
      this.internalValue = relations.map((relation) => {
        const internalRelation = this.internalValue
          .find(rel => rel.destContent && rel.destContent.id === relation.destContent.id);
        if (internalRelation) return internalRelation;
        return relation;
      });
      this.loading = false;
      this.emitValue();
    },
    async acceptZipUploader(contents) {
      this.closeZipUploadDialog();
      contents = this.filterPublishedContents(contents, this.$t('message.content.relation.error.nonPublishedContentSelectedWarning'));
      contents = this.filterContentsByType(contents, this.$t('message.content.relation.error.nonAllowedContentTypeSelectedWarning'));
      if (contents.length === 0) {
        return;
      }

      this.loading = true;
      if (!this.multiple && contents.length > 1) {
        contents = [contents[0]];
      }

      const relations = await this.getRelationFields(contents.map(content => content.id));
      if (!this.multiple) {
        this.internalValue = [];
      }
      this.internalValue = this.insertNewItemsAtBeginningValue
        ? relations.concat(this.internalValue)
        : this.internalValue.concat(relations);
      this.loading = false;
      this.emitValue();
    },
    async getRelationFields(contentIds) {
      return api.getFields({
        relationType: this.relationType,
        entityIds: contentIds,
      });
    },
    /**
     * Updated the relation identified by the given uuid.
     */
    updateRelation(uuid, value, defaultUpdate = false) {
      const index = this.internalValue.findIndex(relation => relation.uuid === uuid);
      const relation = isEmpty(value) ? {} : { ...(this.internalValue[index] || {}), ...value };
      this.internalValue.splice(index, 1, {
        ...relation,
        // Preserve the old uuid
        uuid,
      });
      this.emitValue(defaultUpdate);
    },
    updateRelationWithDefaultValue(uuid, value) {
      this.updateRelation(uuid, value, true);
    },
    addRelation() {
      if (this.insertNewItemsAtBeginningValue) {
        this.internalValue.splice(0, 0, this.createRelationCard());
      } else {
        this.internalValue.push(this.createRelationCard());
        this.$refs.relations.$el.lastChild.scrollIntoView({ behavior: 'smooth' });
      }
    },
    async deleteAll() {
      const confirm = await this.$refs.confirmDeleteAll.open();
      if (!confirm) {
        return;
      }
      this.internalValue = [];
      this.emitValue();
      this.setInternalValue([]);
    },
    remove(index) {
      this.internalValue.splice(index, 1);
      this.emitValue();
    },
    move(from, to) {
      this.internalValue.splice(to, 0, this.internalValue.splice(from, 1)[0]);
      this.emitValue();
    },
    /**
     * Creates a relation card based in the card given.
     * @return {{}}
     */
    createRelationCard() {
      return { uuid: generateUuid() };
    },
    /**
     * Emits the editor's current value.
     */
    emitValue(defaultUpdate = false) {
      const value = cloneDeep(this.internalValue);
      this.$emit(defaultUpdate ? 'default' : 'input', this.multiple ? value : value[0] || null);
    },
    /**
     * Opens the searcher dialog.
     */
    async openContentSearchDialog() {
      this.contentSearchDialogState.render = true;
      await this.$nextTick();
      this.contentSearchDialogState.active = true;
    },
    /**
     * Closes the searcher dialog.
     */
    async closeContentSearchDialog() {
      this.contentSearchDialogState.active = false;
      await this.$nextTick();
      this.contentSearchDialogState.render = false;
    },
    /**
     * Opens the zip uploader dialog.
     */
    async openZipUploadDialog() {
      this.zipUploaderDialogState.render = true;
      await this.$nextTick();
      this.zipUploaderDialogState.active = true;
    },
    /**
     * Closes the zip uploader dialog.
     */
    async closeZipUploadDialog() {
      this.zipUploaderDialogState.active = false;
      await this.$nextTick();
      this.zipUploaderDialogState.render = false;
    },
    /**
     * Updates the component's internal value, giving an key to each of the relations inside the
     * given value. The key helps to keep track of each relation.
     * @param value
     */
    setInternalValue(value) {
      this.internalValue = toArray(cloneDeep(value));
      if (!this.empty) {
        return;
      }
      if (this.emptyItemsByDefault > 0) {
        for (let i = 0; i < this.emptyItemsByDefault; i += 1) {
          this.internalValue.push(this.createRelationCard());
        }
        return;
      }
      if ((!this.disableDelete && !this.disableEmptyInitialCard)
        || this.disableAdd) {
        this.internalValue.push(this.createRelationCard());
      }
    },
  },
};
</script>

<style lang="stylus">
// style
.relation-editor-list-move {
  transition: transform .2s;
}

.relation-editor-list-enter, .relation-editor-list-leave-to {
  opacity: 0;
  transform: translateX(30px);
}

.relation-list {
  &.moving {
    .relation-list-card:not(.sortable-chosen) {
      opacity: .7;
    }
  }
}
</style>
